// Utility functions for caching user ticker list data

export interface CachedListData {
  id: number;
  list_name: string;
  tickers: string[];
  lastUpdated: number;
}

export interface CachedListsData {
  lists: Array<{ 
    id: number; 
    list_name: string; 
    app_user_id: number;
    status: string;
    created_at: string; 
    updated_at: string;
    ticker_count?: number;
  }>;
  lastUpdated: number;
}

const CACHE_KEYS = {
  USER_LISTS: 'stocktable-user-lists-cache',
  LIST_DATA: (listId: number) => `stocktable-list-${listId}-cache`,
  CACHE_INVALIDATION: 'stocktable-cache-invalidation',
  CURRENT_LIST_NAME: 'stocktable-current-list-name',
  CURRENT_LIST_ID: 'stocktable-current-list-id',
} as const;

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Cache user lists
export const cacheUserLists = (lists: Array<{ 
  id: number; 
  list_name: string; 
  app_user_id: number;
  status: string;
  created_at: string; 
  updated_at: string;
  ticker_count?: number;
}>) => {
  const cacheData: CachedListsData = {
    lists,
    lastUpdated: Date.now(),
  };
  localStorage.setItem(CACHE_KEYS.USER_LISTS, JSON.stringify(cacheData));
};

// Get cached user lists
export const getCachedUserLists = (): Array<{ 
  id: number; 
  list_name: string; 
  app_user_id: number;
  status: string;
  created_at: string; 
  updated_at: string;
  ticker_count?: number;
}> | null => {
  try {
    const cached = localStorage.getItem(CACHE_KEYS.USER_LISTS);
    if (!cached) return null;
    
    const cacheData: CachedListsData = JSON.parse(cached);
    const isExpired = Date.now() - cacheData.lastUpdated > CACHE_DURATION;
    const isInvalidated = isCacheInvalidated(cacheData.lastUpdated);
    
    if (isExpired || isInvalidated) {
      localStorage.removeItem(CACHE_KEYS.USER_LISTS);
      return null;
    }
    
    return cacheData.lists;
  } catch (error) {
    console.error('Error reading cached user lists:', error);
    return null;
  }
};

// Cache list data (tickers)
export const cacheListData = (listId: number, listName: string, tickers: string[]) => {
  const cacheData: CachedListData = {
    id: listId,
    list_name: listName,
    tickers,
    lastUpdated: Date.now(),
  };
  localStorage.setItem(CACHE_KEYS.LIST_DATA(listId), JSON.stringify(cacheData));
};

// Get cached list data
export const getCachedListData = (listId: number): { list_name: string; tickers: string[] } | null => {
  try {
    const cached = localStorage.getItem(CACHE_KEYS.LIST_DATA(listId));
    if (!cached) return null;
    
    const cacheData: CachedListData = JSON.parse(cached);
    const isExpired = Date.now() - cacheData.lastUpdated > CACHE_DURATION;
    
    if (isExpired) {
      localStorage.removeItem(CACHE_KEYS.LIST_DATA(listId));
      return null;
    }
    
    return {
      list_name: cacheData.list_name,
      tickers: cacheData.tickers,
    };
  } catch (error) {
    console.error('Error reading cached list data:', error);
    return null;
  }
};

// Clear cache for a specific list (when list is modified)
export const clearListCache = (listId: number) => {
  localStorage.removeItem(CACHE_KEYS.LIST_DATA(listId));
};

// Clear all user lists cache (when lists are modified)
export const clearUserListsCache = () => {
  localStorage.removeItem(CACHE_KEYS.USER_LISTS);
};

// Clear all caches
export const clearAllCaches = () => {
  clearUserListsCache();
  // Clear all list data caches
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith('stocktable-list-') && key.endsWith('-cache')) {
      localStorage.removeItem(key);
    }
  });
};

// Set cache invalidation timestamp (when data is modified)
export const setCacheInvalidation = () => {
  localStorage.setItem(CACHE_KEYS.CACHE_INVALIDATION, Date.now().toString());
};

// Check if cache is invalidated (data was modified since last cache)
export const isCacheInvalidated = (cacheTimestamp: number): boolean => {
  const invalidationTimestamp = localStorage.getItem(CACHE_KEYS.CACHE_INVALIDATION);
  if (!invalidationTimestamp) return false;
  
  const invalidationTime = parseInt(invalidationTimestamp, 10);
  return invalidationTime > cacheTimestamp;
};

// Cache current list name (to prevent flickering on tab switch)
export const cacheCurrentListName = (listName: string) => {
  localStorage.setItem(CACHE_KEYS.CURRENT_LIST_NAME, listName);
};

// Get cached current list name
export const getCachedCurrentListName = (): string | null => {
  return localStorage.getItem(CACHE_KEYS.CURRENT_LIST_NAME);
};

// Clear current list name cache (when switching lists or creating new ones)
export const clearCurrentListNameCache = () => {
  localStorage.removeItem(CACHE_KEYS.CURRENT_LIST_NAME);
};

// Cache current list ID (to maintain selection across tabs)
export const cacheCurrentListId = (listId: number) => {
  localStorage.setItem(CACHE_KEYS.CURRENT_LIST_ID, listId.toString());
};

// Get cached current list ID
export const getCachedCurrentListId = (): number | null => {
  const cachedId = localStorage.getItem(CACHE_KEYS.CURRENT_LIST_ID);
  return cachedId ? parseInt(cachedId, 10) : null;
};

// Clear current list ID cache (when creating new lists)
export const clearCurrentListIdCache = () => {
  localStorage.removeItem(CACHE_KEYS.CURRENT_LIST_ID);
};

// Generate a unique "New List" name based on existing lists
export const generateUniqueListName = (existingLists: Array<{ list_name: string }>): string => {
  const baseName = 'New List';
  
  // Check if "New List" is available
  const hasBaseName = existingLists.some(list => list.list_name === baseName);
  if (!hasBaseName) {
    return baseName;
  }
  
  // Find the next available number
  let counter = 1;
  while (true) {
    const candidateName = `${baseName} (${counter})`;
    const exists = existingLists.some(list => list.list_name === candidateName);
    if (!exists) {
      return candidateName;
    }
    counter++;
  }
};
